import React from 'react';
import { Switch, Route } from 'react-router-dom';

// Layouts
import PublicLayout from '../layouts/PublicLayout';
import DashboardLayout from '../layouts/DashboardLayout';

// Routing Components
import PrivateRoute from '../components/routing/PrivateRoute';
import PermissionRoute from '../components/routing/PermissionRoute';

// Public Pages
import Landing from '../pages/public/Landing';
import TemplateList from '../pages/public/TemplateList';
import PublicDemoPage from '../pages/public/PublicDemoPage';
import About from '../pages/public/About';
import Contact from '../pages/public/Contact';
import FAQ from '../pages/public/FAQ';
import Pricing from '../pages/public/Pricing';
import Privacy from '../pages/public/Privacy';
import Terms from '../pages/public/Terms';
import KVKK from '../pages/public/KVKK';

import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import Dashboard from '../pages/Dashboard';
import ProfilePage from '../pages/profile/ProfilePage';
import CompanyUsersPage from '../pages/company/CompanyUsersPage';
import CompanySettingsPage from '../pages/company/CompanySettingsPage';
import InvitesPage from '../pages/company/InvitesPage';
import InviteAcceptPage from '../pages/company/InviteAcceptPage';
import RegisterWithInvitePage from '../pages/company/RegisterWithInvitePage';
import MyInvitesPage from '../pages/dashboard/MyInvitesPage';
import PermissionsPage from '../pages/admin/PermissionsPage';
import EmailLogsPage from '../pages/admin/EmailLogsPage';
import EmailTemplateListPage from '../pages/admin/EmailTemplateListPage';
import EmailTemplateEditorPage from '../pages/admin/EmailTemplateEditorPage';
import AppSettingListPage from '../pages/admin/AppSettingListPage';
import AppSettingEditorPage from '../pages/admin/AppSettingEditorPage';
import PermissionGroupsPage from '../pages/company/PermissionGroupsPage';
import UserPermissionAssignmentPage from '../pages/company/UserPermissionAssignmentPage';
import BillingPage from '../pages/billing/BillingPage';
import ForbiddenPage from '../pages/ForbiddenPage';
import CreditHistoryPage from '../pages/CreditHistoryPage';
import CreditManagePage from '../pages/admin/CreditManagePage';
import CreditPackagesPage from '../pages/admin/CreditPackagesPage';
import CreditReportsPage from '../pages/admin/CreditReportsPage';
import CategoryManagementPage from '../pages/admin/CategoryManagementPage';

/**
 * Centralized route definitions
 * 
 * CRITICAL: This project uses React Router 5!
 * - Use <Switch> instead of <Routes>
 * - Use component/render prop instead of element prop
 * - Use useHistory instead of useNavigate
 * 
 * Layout types: 'public', 'dashboard', 'none'
 */
const publicRoutes = [
  {
    path: '/',
    component: Landing,
    exact: true,
    title: 'Ana Sayfa',
    layout: 'public'
  },
  {
    path: '/publictemplates',
    component: TemplateList,
    exact: true,
    title: 'Şablonlar',
    layout: 'public'
  },
  {
    path: '/demo',
    component: PublicDemoPage,
    exact: true,
    title: 'Demo',
    layout: 'public'
  },
  {
    path: '/about',
    component: About,
    exact: true,
    title: 'Hakkımızda',
    layout: 'public'
  },
  {
    path: '/contact',
    component: Contact,
    exact: true,
    title: 'İletişim',
    layout: 'public'
  },
  {
    path: '/faq',
    component: FAQ,
    exact: true,
    title: 'Sıkça Sorulan Sorular',
    layout: 'public'
  },
  {
    path: '/pricing',
    component: Pricing,
    exact: true,
    title: 'Fiyatlandırma',
    layout: 'public'
  },
  {
    path: '/privacy',
    component: Privacy,
    exact: true,
    title: 'Gizlilik Politikası',
    layout: 'public'
  },
  {
    path: '/terms',
    component: Terms,
    exact: true,
    title: 'Kullanım Koşulları',
    layout: 'public'
  },
  {
    path: '/kvkk',
    component: KVKK,
    exact: true,
    title: 'KVKK Aydınlatma Metni',
    layout: 'public'
  },
];

const protectedRoutes = [
  {
    path: '/dashboard',
    component: Dashboard,
    exact: true,
    title: 'Dashboard',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/profile',
    component: ProfilePage,
    exact: true,
    title: 'Profil',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/my-invites',
    component: MyInvitesPage,
    exact: true,
    title: 'Davetlerim',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/admin/permissions',
    component: PermissionsPage,
    exact: true,
    title: 'Yetki Yönetimi',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/admin/email-logs',
    component: EmailLogsPage,
    exact: true,
    title: 'Email Logları',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.email-logs.view'
  },
  {
    path: '/admin/email-templates',
    component: EmailTemplateListPage,
    exact: true,
    title: 'Email Şablonları',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.email-templates.view'
  },
  {
    path: '/admin/email-templates/new',
    component: EmailTemplateEditorPage,
    exact: true,
    title: 'Yeni Email Şablonu',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.email-templates.create'
  },
  {
    path: '/admin/email-templates/:id/edit',
    component: EmailTemplateEditorPage,
    exact: true,
    title: 'Email Şablonu Düzenle',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.email-templates.edit'
  },
  {
    path: '/admin/app-settings',
    component: AppSettingListPage,
    exact: true,
    title: 'Sistem Ayarları',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.app-settings.view'
  },
  {
    path: '/admin/app-settings/new',
    component: AppSettingEditorPage,
    exact: true,
    title: 'Yeni Sistem Ayarı',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.app-settings.create'
  },
  {
    path: '/admin/app-settings/:id/edit',
    component: AppSettingEditorPage,
    exact: true,
    title: 'Sistem Ayarı Düzenle',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'admin.app-settings.edit'
  },
  {
    path: '/templates',
    component: require('../pages/templates/TemplateListPage').default,
    exact: true,
    title: 'Şablonlar',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/templates/new',
    component: require('../pages/templates/TemplateEditorPage').default,
    exact: true,
    title: 'Yeni Şablon',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/templates/:templateId/edit',
    component: require('../pages/templates/TemplateEditorPage').default,
    exact: true,
    title: 'Şablon Düzenle',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/templates/:templateId/preview',
    component: require('../pages/templates/TemplateEditorPage').default,
    exact: true,
    title: 'Şablon Önizleme',
    layout: 'dashboard',
    requiresAuth: true
  },
    {
      path: '/documents',
      component: require('../pages/DocumentListPage').default,
      exact: true,
      title: 'Dokümanlar',
      layout: 'dashboard',
      requiresAuth: true
    },
    {
      path: '/documents/new',
      component: require('../pages/DocumentTemplateSelectionPage').default,
      exact: true,
      title: 'Şablon Seçin',
      layout: 'dashboard',
      requiresAuth: true
    },
    {
      path: '/documents/new/:templateId',
      component: require('../pages/DocumentTemplateInfoPage').default,
      exact: true,
      title: 'Şablon Bilgileri',
      layout: 'dashboard',
      requiresAuth: true
    },
    {
      path: '/documents/wizard/:templateId',
      component: require('../pages/DocumentWizardPage').default,
      exact: true,
      title: 'Belge Oluştur',
      layout: 'dashboard',
      requiresAuth: true
    },
  {
    path: '/documents/:id',
    component: require('../pages/DocumentViewPage').default,
    exact: true,
    title: 'Doküman Görüntüle',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/users',
    component: CompanyUsersPage,
    exact: true,
    title: 'Kullanıcılar',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/invites',
    component: InvitesPage,
    exact: true,
    title: 'Davetler',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/companies',
    component: CompanySettingsPage,
    exact: true,
    title: 'Şirket Ayarları',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/permission-groups',
    component: PermissionGroupsPage,
    exact: true,
    title: 'Yetki Grupları',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/permissions/assign/:userId',
    component: UserPermissionAssignmentPage,
    exact: true,
    title: 'Kullanıcı Yetkileri',
    layout: 'dashboard',
    requiresAuth: true
  },
  {
    path: '/billing',
    component: BillingPage,
    exact: true,
    title: 'Ödeme & Fatura',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'billing.view'
  },
  {
    path: '/credits/history',
    component: CreditHistoryPage,
    exact: true,
    title: 'Kredi Geçmişi',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'credits.view'
  },
  {
    path: '/credits/manage',
    component: CreditManagePage,
    exact: true,
    title: 'Kredi Yönetimi',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'credits.manage'
  },
  {
    path: '/credits/packages',
    component: CreditPackagesPage,
    exact: true,
    title: 'Kredi Paketleri',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'credit-packages.manage'
  },
  {
    path: '/credits/reports',
    component: CreditReportsPage,
    exact: true,
    title: 'Kredi Raporları',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'credits.reports'
  },
  {
    path: '/admin/template-categories',
    component: CategoryManagementPage,
    exact: true,
    title: 'Şablon Kategori Yönetimi',
    layout: 'dashboard',
    requiresAuth: true,
    permission: 'templatecategories.manage'
  },
  {
    path: '/invite/accept/:token',
    component: InviteAcceptPage,
    exact: true,
    title: 'Davet Kabul',
    layout: 'dashboard',
    requiresAuth: true
  }
];

const noLayoutRoutes = [
  {
    path: '/invite/register/:token',
    component: RegisterWithInvitePage,
    exact: true,
    title: 'Davet ile Kayıt',
    layout: 'none'
  },
  {
    path: '/login',
    component: LoginPage,
    exact: true,
    title: 'Giriş',
    layout: 'none'
  },
  {
    path: '/register',
    component: RegisterPage,
    exact: true,
    title: 'Kayıt',
    layout: 'none'
  },
  {
    path: '/forgot-password',
    component: ForgotPasswordPage,
    exact: true,
    title: 'Şifremi Unuttum',
    layout: 'none'
  },
  {
    path: '/reset-password/:token',
    component: ResetPasswordPage,
    exact: true,
    title: 'Şifre Sıfırla',
    layout: 'none'
  },
  {
    path: '/verify-email/:token',
    component: VerifyEmailPage,
    exact: true,
    title: 'Email Doğrula',
    layout: 'none'
  },
  {
    path: '/403',
    component: ForbiddenPage,
    exact: true,
    title: 'Erişim Reddedildi',
    layout: 'none'
  }
];

const getLayoutComponent = (layoutType) => {
  switch (layoutType) {
    case 'public':
      return PublicLayout;
    case 'dashboard':
      return DashboardLayout;
    case 'none':
    default:
      return null;
  }
};

const renderRoute = (route, isProtected = false) => {
  const { path, component: Component, exact, layout = 'none', permission } = route;
  const Layout = getLayoutComponent(layout);
  
  const renderComponent = () => {
    if (Layout) {
      return (
        <Layout>
          <Component />
        </Layout>
      );
    }
    return <Component />;
  };
  
  if (isProtected) {
    // Use PermissionRoute if permission is specified
    if (permission) {
      return (
        <PermissionRoute
          key={path}
          exact={exact}
          path={path}
          component={Component}
          permission={permission}
          layout={layout}
        />
      );
    }
    
    // Otherwise use PrivateRoute
    return (
      <PrivateRoute
        key={path}
        exact={exact}
        path={path}
        component={Component}
        layout={layout}
      />
    );
  }
  
  return (
    <Route
      key={path}
      exact={exact}
      path={path}
      render={renderComponent}
    />
  );
};

export const PublicRoutes = () => {
  return (
    <Switch>
      {protectedRoutes.map(route => renderRoute(route, true))}
      {noLayoutRoutes.map(route => renderRoute(route, false))}
      {publicRoutes.map(route => renderRoute(route, false))}
    </Switch>
  );
};

export const AppRoutes = () => {
  return <PublicRoutes />;
};

export default AppRoutes;

